import { computed, effect, inject, Injectable, linkedSignal, Signal } from '@angular/core';
import { FormGroup, NonNullableFormBuilder } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { DaysOfWeek, DivisionConfig, Play, PlayExecution, ScheduleSettings } from '@r1-compass-library/models';
import { PlaySchedulingForm } from '@r1-compass-library/models/play/play-scheduling-form.model';
import { DateTime } from 'luxon';
import { PlayStore } from '../store/play.store';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import { FormValueOf, formValueSignal } from '@r1-compass-library/utilities';
import { noopFormSaveMutationResult } from '@r1-compass-library/guards/hasUnsavedChanges.guard';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { AppConstants } from '../../app.constants';
import { PlayValidationStateService } from '../play-validation-state.service';

export type PlayScheduleFormValue = FormValueOf<FormGroup<PlaySchedulingForm>>;

@Injectable()
export class PlaySchedulingFormService {
    readonly #nonNullableFormBuilder = inject(NonNullableFormBuilder);
    readonly #store = inject(PlayStore);
    readonly #globalLoggedInUserStore = inject(GlobalLoggedInUserStore);
    readonly #validationService = inject(PlayValidationStateService);

    protected readonly days = AppConstants.days;

    public playSchedulingForm: FormGroup<PlaySchedulingForm> = this.#nonNullableFormBuilder.group<PlaySchedulingForm>({
        offset: this.#nonNullableFormBuilder.control(''),
        startTime: this.#nonNullableFormBuilder.control(''),
        daysOfWeek: this.#nonNullableFormBuilder.control<DaysOfWeek[]>([]),
        startDate: this.#nonNullableFormBuilder.control<Date | undefined>(undefined),
        endDate: this.#nonNullableFormBuilder.control<Date | undefined>(undefined),
        override: this.#nonNullableFormBuilder.control(false),
        isEnabled: this.#nonNullableFormBuilder.control(false),
    });

    public playScheduleFormGroupValue = formValueSignal<PlayScheduleFormValue>(this.playSchedulingForm);

    public displayTime = computed<string>(() => this.transformedPM());
    public displayOffset = computed<string>(() => this.playScheduleFormGroupValue().offset);

    public transformedPM = computed<string>(() => {
        const time = this.playScheduleFormGroupValue().startTime;
        if (time) {
            const parsedTime = DateTime.fromFormat(time, 'HH:mm');
            return parsedTime.isValid ? parsedTime.toFormat('h:mm a') : '';
        }
        return '';
    });

    public inCheckedDays = computed<boolean[]>(() => {
        const checkedDays = this.#checkedDays();
        return this.days.map(day =>
            checkedDays.map(checkedDay => checkedDay.toLowerCase()).includes(day.toLowerCase())
        );
    });

    #checkedDays = linkedSignal<DaysOfWeek[]>(() => {
        const executionData: PlayExecution | undefined = this.play()?.config?.execution;
        const divisionSchedule = this.divisionConfig()?.config?.schedule;

        return this.initializeExecutionData(executionData, divisionSchedule).daysOfWeek;
    });
    public checkedDays = this.#checkedDays.asReadonly();

    private divisionConfig: Signal<DivisionConfig | undefined> = this.#globalLoggedInUserStore.selectedDivisionConfig;
    private play = this.#store.selectSignal<Play | undefined>(store => store.play);

    constructor() {
        effect(
            () => {
                const play = this.#store.play();
                const schedule = this.divisionConfig()?.config?.schedule;

                if (play) this.initPlayScheduleForm(play, schedule);
            },
            { debugName: 'init schedule form' }
        );
    }

    public updateDaysOfTheWeekCheckbox(matCheckboxChange: MatCheckboxChange, day: DaysOfWeek) {
        const verifiedIsInCheckedDays =
            this.#checkedDays().findIndex(checkedDay => checkedDay.toLowerCase() === day.toLowerCase()) > -1;

        if (matCheckboxChange.checked && !verifiedIsInCheckedDays) {
            this.#checkedDays.update(days => [...days, day]);
        } else if (!matCheckboxChange.checked && verifiedIsInCheckedDays) {
            const checkedDayIndex = this.#checkedDays().findIndex(
                checkedDay => checkedDay.toLowerCase() === day.toLowerCase()
            );
            this.#checkedDays.update(days => [...days.toSpliced(checkedDayIndex, 1)]);
        }

        this.markDirtyTouchedAndPatch({ daysOfWeek: this.#checkedDays() });
    }

    public onOverrideScheduleToggled(event: MatSlideToggleChange) {
        this.markDirtyTouchedAndPatch({ override: event.checked });
    }

    /**
     * Method to transform user selected offset into UTC format.
     * @param event User selected offset
     */
    public transformOffset(event: MatSelectChange) {
        const utcOffset = event.value;

        this.markDirtyTouchedAndPatch({ offset: utcOffset });
    }

    public transformTimeFormat(time: string) {
        if (!time) {
            return;
        }
        console.log('transformTimeFormat', time);
        const parsedTime = DateTime.fromFormat(time, 'h:mm a');
        if (!parsedTime.isValid) {
            return;
        }
        // const transformed24hr = DateTime.fromFormat(time, 'h:mm a').toFormat('HH:mm');
        // const twentyFourHourTime = transformed24hr;

        this.markDirtyTouchedAndPatch({ startTime: parsedTime.toFormat('HH:mm') });
    }

    public onSave() {
        const play = this.play();

        const { isEnabled } = this.playSchedulingForm.getRawValue();
        let { ...execution }: PlayExecution = this.playSchedulingForm.getRawValue();
        const userId = this.#globalLoggedInUserStore.selectedUserId();

        if (play && userId) {
            // If override is turned off (set to false), we want to avoid saving any PlayExecution data.
            // Doing it this way avoids the UI getting stuck displaying incorrect 'Next Run Time' info after a save.
            // Doing a PATCH to clear the data will break it until page reloads.
            if (!execution.override) {
                this.#checkedDays.set([]); // Clears checked days to avoid UI bug where old values stay checked in schedule until refresh
                execution = this.clearExecutionData();
            }
            const playToSave = this.formatPlayToSave(play, isEnabled, userId);

            return this.#store.savePlayAndData({
                play: playToSave,
                config: { execution: execution },
            });
        } else {
            return noopFormSaveMutationResult();
        }
    }

    public setDate(date: Date | undefined, type: 'startDate' | 'endDate') {
        if (type === 'startDate') {
            this.markDirtyTouchedAndPatch({ startDate: date });

            const endDate = this.playSchedulingForm.controls.endDate.value;
            if (date && endDate && DateTime.fromJSDate(endDate) < DateTime.fromJSDate(date)) {
                this.markDirtyTouchedAndPatch({ endDate: undefined });
            }
        } else if (type === 'endDate') {
            this.markDirtyTouchedAndPatch({ endDate: date });
        }
    }

    private formatPlayToSave(play: Play, isEnabled: boolean, userId: number) {
        const valid = this.#validationService.invalidSections().length === 0;

        const playToSave: Partial<Play> = {
            id: play.id,
            enabled: isEnabled,
            updatedBy: userId,
            lastConfigured: new Date(),
            valid: valid,
            // If play is now enabled, but wasn't before, then update lastActivated
            ...(isEnabled === true && play?.enabled !== true && { lastActivated: new Date() }),
        };

        return playToSave;
    }

    private initializeExecutionData(
        playExecutionData: PlayExecution | undefined,
        divisionConfigSchedule: ScheduleSettings | undefined
    ) {
        const checkForEmptyValues = (executionData: PlayExecution) => {
            const scheduleCompare = executionData.daysOfWeek.toString() === '';
            const startTimeCompare = executionData.startTime === '';
            const offsetCompare = executionData.offset === '';
            return scheduleCompare && startTimeCompare && offsetCompare;
        };

        // If executionData exists but is full of empty values, it was saved with override turned off.
        if (playExecutionData && !checkForEmptyValues(playExecutionData)) {
            return playExecutionData;
        } else {
            // If it was saved with override turned off, populate the Form with Division Schedule data instead.
            // This will get the 'Next Run Time' to operate based off of the Division Schedule data.
            const divisionExecutionData: PlayExecution = {
                offset: divisionConfigSchedule?.offset,
                startTime: divisionConfigSchedule?.startTime,
                daysOfWeek: divisionConfigSchedule?.daysOfWeek ?? [],
                startDate: undefined,
                endDate: undefined,
                override: false,
            };
            return divisionExecutionData;
        }
    }

    /**
     * @description With the amount of boilerplate interactions like this, I figure this makes for good consistency
     */
    private markDirtyTouchedAndPatch(value: Partial<PlayScheduleFormValue>) {
        this.playSchedulingForm.patchValue(value);
        this.playSchedulingForm.markAllAsDirty();
        this.playSchedulingForm.markAllAsTouched();
    }

    private clearExecutionData() {
        const execution: PlayExecution = {
            offset: '',
            startTime: '',
            daysOfWeek: [],
            startDate: undefined,
            endDate: undefined,
            override: false,
        };
        return execution;
    }

    private initPlayScheduleForm(play: Play, divisionSchedule: ScheduleSettings | undefined) {
        const executionData = this.generateInitialExecutionData(
            this.initializeExecutionData(play?.config?.execution, divisionSchedule)
        );

        this.playSchedulingForm.patchValue({
            ...executionData,
            isEnabled: play.enabled,
        });
    }

    /**
     * @description Populates the Play Scheduling Form either with saved PlayExecution data, or Division schedule data.
     * Will use PlayExecution data if Schedule is saved with 'override' turned on, otherwise will use Division data.
     * @param executionData - The data being used to populate the form.
     */
    private generateInitialExecutionData(executionData: PlayExecution) {
        return {
            offset: executionData.offset ?? '',
            startTime: executionData.startTime ?? '',
            daysOfWeek: executionData.daysOfWeek ?? [],
            startDate: executionData.startDate ? new Date(executionData.startDate) : undefined,
            endDate: executionData.endDate ? new Date(executionData.endDate) : undefined,
            override: executionData.override ?? false,
        };
    }
}
